import { ChangeEvent, ComponentPropsWithRef, ComponentType, ElementType, useCallback, useContext, useEffect, useMemo } from 'react';
import { Controller, ControllerFieldState, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';

import { FormContext } from '@/common/Form';
import Input from '@/components/Input';

export type FormInputProps<T extends ElementType> = {
  name: string;
  as?: T | ComponentType<any>;
  validate?: boolean;
} & ComponentPropsWithRef<T>;

type FormInputValidationRegistrarProps = {
  name: string;
  isValidating: boolean;
  setValidatingFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  children: React.ReactNode;
};

const FormInputValidationRegistrar = ({ name, isValidating, setValidatingFields, children }: FormInputValidationRegistrarProps) => {
  useEffect((): (() => void) => {
    setValidatingFields((current: Set<string>): Set<string> => {
      const updatedSet = new Set(current);
      if (isValidating) {
        updatedSet.add(name);
      } else {
        updatedSet.delete(name);
      }
      return updatedSet;
    });
    return (): void => {
      setValidatingFields((current: Set<string>): Set<string> => {
        const updatedSet = new Set(current);
        updatedSet.delete(name);
        return updatedSet;
      });
    };
  }, [isValidating, name, setValidatingFields]);
  return <>{children}</>;
};

const FormInput = ({ name, as: As = Input, validate, ...inputProps }: FormInputProps<typeof As>): JSX.Element => {
  const { control } = useFormContext();
  const {
    validatingFields: [, setValidatingFields],
  } = useContext(FormContext);
  const isValidating = useMemo(
    (): boolean => inputProps.type !== 'hidden' && validate !== undefined && validate !== false,
    [validate, inputProps.type]
  );

  const render = useCallback(
    ({ field, fieldState }: { field: ControllerRenderProps<FieldValues, any>; fieldState: ControllerFieldState }): JSX.Element => {
      const { invalid, isDirty, error } = fieldState;
      const { onChange: change, onBlur: _onBlur, ref: _ref, ...fieldProps } = field;
      const validityProps = {
        isValid: invalid === false,
        isInvalid: error !== undefined,
        isDirty,
        feedback: error?.message,
      };

      const onChange = (event: ChangeEvent<HTMLInputElement>): boolean => {
        if (inputProps.onChange) {
          const result = inputProps.onChange(event);
          if (result !== undefined) event = result;
        }
        const value =
          event?.target && event?.target?.name !== undefined && event?.target?.value !== undefined
            ? event
            : { target: { name, value: event ?? '' } };
        change(value);
        return inputProps.multiple !== undefined && inputProps.multiple !== false;
      };

      return <As {...inputProps} {...fieldProps} {...(isValidating ? validityProps : {})} onChange={onChange} />;
    },
    [name, inputProps, As, isValidating]
  );

  return (
    <FormInputValidationRegistrar key={name} name={name} isValidating={isValidating} setValidatingFields={setValidatingFields}>
      <Controller name={name} control={control} render={render} />
    </FormInputValidationRegistrar>
  );
};

export default FormInput;
